import { useCallback, useEffect, useMemo, useRef, useState } from "react"

interface UseImageSelectionResult {
  selectedIndices: Set<number>
  toggleImageSelection: (idx: number) => void
  selectAll: () => void
  clearAll: () => void
  isSelected: (idx: number) => boolean
  selectedCount: number
  getSelectedUrls: (allUrls: string[]) => string[]
}

const getUrlsKey = (urls: string[]) => urls.join("\u0000")

const useImageSelection = (urls: string[]): UseImageSelectionResult => {
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set())
  const prevUrlsKey = useRef(getUrlsKey(urls))

  useEffect(() => {
    const nextKey = getUrlsKey(urls)
    if (prevUrlsKey.current !== nextKey) {
      setSelectedIndices(new Set())
      prevUrlsKey.current = nextKey
    }
  }, [urls])

  const toggleImageSelection = useCallback((idx: number) => {
    setSelectedIndices(prev => {
      const next = new Set(prev)
      next.has(idx) ? next.delete(idx) : next.add(idx)
      return next
    })
  }, [])

  const selectAll = useCallback(() => {
    setSelectedIndices(new Set(urls.map((_, idx) => idx)))
  }, [urls])

  const clearAll = useCallback(() => {
    setSelectedIndices(new Set())
  }, [])

  const isSelected = useCallback((idx: number) => selectedIndices.has(idx), [selectedIndices])

  const selectedCount = useMemo(() => selectedIndices.size, [selectedIndices])

  const getSelectedUrls = useCallback(
    (allUrls: string[]) =>
      Array.from(selectedIndices)
        .filter(idx => idx >= 0 && idx < allUrls.length)
        .map(idx => allUrls[idx]),
    [selectedIndices]
  )

  return {
    selectedIndices,
    toggleImageSelection,
    selectAll,
    clearAll,
    isSelected,
    selectedCount,
    getSelectedUrls
  }
}

export { useImageSelection }
